import { Flex, Heading, Icon, VStack } from '@chakra-ui/react';
import { ChevronDownIcon, FileCheck2Icon, FileIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { ChecklistItem } from '../utils/buildChecklistItems';
import { ChecklistItemRow } from './ChecklistItemRow';

interface OtherDocumentsListProps {
  items: ChecklistItem[];
  /** The active checklist item's id (the item containing the selected tab). */
  activeItemId?: string;
  /** Selects a checklist item by opening its first document tab. */
  onSelectDocument: (ingestionDocumentId: string) => void;
}

/**
 * Left-rail "Other documents" section: the checklist items that don't need
 * review (auto-approved / already handled). Collapsible and collapsed by
 * default so the rail leads with the Needs-Review work. Renders nothing when
 * empty.
 */
export const OtherDocumentsList: FC<OtherDocumentsListProps> = ({
  items,
  activeItemId,
  onSelectDocument,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Flex
        align="center"
        aria-controls="other-documents-panel"
        aria-expanded={expanded}
        cursor="pointer"
        justify="space-between"
        px={2.5}
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((previous) => !previous)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((previous) => !previous);
          }
        }}
      >
        <Heading
          color="compliance.text.secondary"
          fontSize="md"
          fontWeight="semibold"
          size="headline"
        >
          Other documents
        </Heading>
        <Icon
          as={ChevronDownIcon}
          boxSize="18px"
          color="compliance.text.secondary"
          flexShrink={0}
          transform={expanded ? undefined : 'rotate(-90deg)'}
          transition="transform 0.15s ease"
        />
      </Flex>

      {expanded && (
        <VStack align="stretch" id="other-documents-panel" spacing={1.5}>
          {items.map((item) => (
            <ChecklistItemRow
              key={item.id}
              // Standalone documents (not attached to any SkySlope checklist
              // requirement) get a plain-file icon + "Not on checklist" caption
              // to set them apart from auto-approved checklist items.
              caption={item.isChecklistItem ? undefined : 'Not on checklist'}
              icon={item.isChecklistItem ? FileCheck2Icon : FileIcon}
              isSelected={item.id === activeItemId}
              title={item.title}
              onSelect={() =>
                onSelectDocument(item.docs[0].ingestionDocumentId)
              }
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};
